"use client";

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Search, X, Loader2 } from "lucide-react";
import type { BlogCollection } from "@/lib/blogs";

export interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  collection: BlogCollection;
  collectionLabel: string;
  date: string;
  readTime: string;
  image: string;
  author: string;
}

interface BlogSearchContextValue {
  query: string;
  setQuery: (q: string) => void;
  filtered: BlogPostSummary[];
  isSearching: boolean;
}

const BlogSearchContext = createContext<BlogSearchContextValue | null>(null);

export function useBlogSearch() {
  const ctx = useContext(BlogSearchContext);
  if (!ctx) {
    return {
      query: "",
      setQuery: () => {},
      filtered: [] as BlogPostSummary[],
      isSearching: false,
    };
  }
  return ctx;
}

export function BlogSearchProvider({
  posts,
  children,
}: {
  posts: BlogPostSummary[];
  children: ReactNode;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query);
    }, 180);
    return () => clearTimeout(timer);
  }, [query]);

  const isSearching = query !== debounced;

  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.collection.toLowerCase().includes(q) ||
        p.collectionLabel.toLowerCase().includes(q),
    );
  }, [debounced, posts]);

  return (
    <BlogSearchContext.Provider value={{ query, setQuery, filtered, isSearching }}>
      {children}
    </BlogSearchContext.Provider>
  );
}

function SearchInput() {
  const { query, setQuery, isSearching } = useBlogSearch();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      if (query) {
        setQuery("");
      } else {
        inputRef.current?.blur();
      }
    }
  };

  return (
    <div className="relative">
      <div
        className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        aria-hidden="true"
      >
        <Search size={20} />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleInputKeyDown}
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
        aria-label="Search blog posts"
        placeholder="Search guides, tools, proxies..."
        className="w-full h-14 pl-12 pr-20 rounded-2xl bg-surface-container border border-outline-variant/40 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10 transition-colors"
      />
      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
        {isSearching ? (
          <Loader2 size={18} className="animate-spin text-primary" role="status" aria-label="Searching..." />
        ) : query ? (
          <button
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
            aria-label="Clear search"
            title="Clear search"
          >
            <X size={18} />
          </button>
        ) : (
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-outline-variant/30 bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
            <span className="text-xs">/</span>
          </kbd>
        )}
      </div>
    </div>
  );
}

function CategoryChips() {
  const { query, setQuery, filtered } = useBlogSearch();
  const categories = useMemo(() => {
    const map = new Map<string, number>();
    filtered.forEach((p) => map.set(p.category, (map.get(p.category) || 0) + 1));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [filtered]);

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground mr-1">Filter by topic:</span>
      {categories.map(([category, count]) => {
        const active = query.toLowerCase() === category.toLowerCase();
        return (
          <button
            key={category}
            onClick={() => setQuery(active ? "" : category)}
            aria-pressed={active}
            aria-label={active ? `Remove ${category} filter` : `Filter by ${category}`}
            title={active ? `Remove ${category} filter` : `Filter by ${category}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 ${
              active
                ? "bg-primary text-on-primary"
                : "bg-surface-container border border-outline-variant/30 text-muted-foreground hover:text-foreground"
            }`}
          >
            {category} ({count})
          </button>
        );
      })}
    </div>
  );
}

function ResultCount({ total }: { total: number }) {
  const { query, filtered } = useBlogSearch();
  if (!query) return null;
  return (
    <p className="mt-4 text-sm text-muted-foreground" aria-live="polite">
      {filtered.length} of {total} result{filtered.length !== 1 ? "s" : ""} for &ldquo;{query}&rdquo;
    </p>
  );
}

export function BlogSearchBar({ totalPosts }: { totalPosts: number }) {
  return (
    <div className="w-full max-w-3xl mx-auto" role="search">
      <SearchInput />
      <CategoryChips />
      <ResultCount total={totalPosts} />
    </div>
  );
}
